<template>
  <div @keydown.enter.stop.prevent>
    <HiddenFields :value="formValue" />

    <div class="w-full flex flex-col">
      <!-- Payable summary fields -->
      <div class="payable-summary-row">
        <div class="payable-summary-label">
          Payable Amount
        </div>
        <div
          class="payable-summary-value"
          :class="{ 'border-red-500': hasError('amount') }"
        >
          <input
            class="field-input w-full border-none rounded-none"
            type="text"
            inputmode="decimal"
            v-model="amount"
          />
          <div v-if="hasError('amount')" class="px-2 pb-1 text-red-500 text-xs">
            {{ getError('amount') }}
          </div>
        </div>
      </div>

      <div class="payable-summary-row">
        <div class="payable-summary-label">
          Payable GST
        </div>
        <div
          class="payable-summary-value"
          :class="{ 'border-red-500': hasError('gst') }"
        >
          <input
            class="field-input w-full border-none rounded-none"
            type="text"
            inputmode="decimal"
            v-model="gst"
          />
          <div v-if="hasError('gst')" class="px-2 pb-1 text-red-500 text-xs">
            {{ getError('gst') }}
          </div>
        </div>
      </div>

      <!-- Section title -->
      <div
        class="w-full font-semibold p-2 flex border-gray-500 border border-solid text-gray-700 text-sm bg-gray-100"
      >
        Expense Items
      </div>

      <!-- Desktop / tablet table -->
      <div class="expense-desktop w-full overflow-x-auto">
        <div class="expense-table">
          <!-- Header -->
          <div class="expense-table-row expense-table-header font-medium">
            <div class="expense-col-job expense-cell">Job ID</div>
            <div class="expense-col-account expense-cell">Account</div>
            <div class="expense-col-description expense-cell">Description</div>
            <div class="expense-col-tax expense-cell">Tax Code</div>
            <div class="expense-col-amount expense-cell">Amount (Ex. GST)</div>
            <div class="expense-col-action expense-cell">&nbsp;</div>
          </div>

          <!-- Rows -->
          <div
            class="expense-table-row"
            v-for="(item, index) in items"
            :key="`desktop-${item.id || index}`"
          >
            <div
              class="expense-col-job expense-field-cell"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.job_id`) }"
            >
              <JobSelect
                class="field-input m-0 border-none rounded-none w-full"
                :ref="`desktopFocusMe${index}`"
                v-model="item.job_id"
              />
              <div
                v-if="hasError(`expense_items.${index}.job_id`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.job_id`) }}
              </div>
            </div>

            <div
              class="expense-col-account expense-field-cell"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.ledger_account_id`) }"
            >
              <AccountSelect
                class="field-input m-0 border-none rounded-none w-full"
                v-model="item.ledger_account_id"
                @account-selected="onAccountSelected(index, $event)"
              />
              <div
                v-if="hasError(`expense_items.${index}.ledger_account_id`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.ledger_account_id`) }}
              </div>
            </div>

            <div
              class="expense-col-description expense-field-cell"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.description`) }"
            >
              <input
                class="field-input w-full border-none rounded-none"
                type="text"
                v-model="item.description"
              />
              <div
                v-if="hasError(`expense_items.${index}.description`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.description`) }}
              </div>
            </div>

            <div
              class="expense-col-tax expense-field-cell"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.tax_code`) }"
            >
              <TaxCodeSelect
                class="field-input m-0 border-none rounded-none w-full"
                v-model="item.tax_code"
              />
              <div
                v-if="hasError(`expense_items.${index}.tax_code`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.tax_code`) }}
              </div>
            </div>

            <div
              class="expense-col-amount expense-field-cell"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.amount`) }"
            >
              <input
                class="field-input w-full border-none rounded-none"
                type="text"
                inputmode="decimal"
                v-model="item.amount"
              />
              <div
                v-if="hasError(`expense_items.${index}.amount`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.amount`) }}
              </div>
            </div>

            <div class="expense-col-action expense-action-cell">
              <button
                type="button"
                class="w-full h-full text-red-500"
                aria-label="Remove expense"
                @click="removeLine(index)"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile cards -->
      <div class="expense-mobile">
        <div
          class="expense-mobile-card"
          v-for="(item, index) in items"
          :key="`mobile-${item.id || index}`"
        >
          <div class="expense-mobile-card-header">
            <span>Expense {{ index + 1 }}</span>
            <button
              type="button"
              class="expense-mobile-remove"
              aria-label="Remove expense"
              @click="removeLine(index)"
            >
              <i class="fa fa-times mr-1"></i>
              Remove
            </button>
          </div>

          <div class="expense-mobile-field">
            <label>Job ID</label>
            <div
              class="expense-mobile-control"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.job_id`) }"
            >
              <JobSelect
                class="field-input m-0 border-none rounded-none w-full"
                :ref="`mobileFocusMe${index}`"
                v-model="item.job_id"
              />
              <div
                v-if="hasError(`expense_items.${index}.job_id`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.job_id`) }}
              </div>
            </div>
          </div>

          <div class="expense-mobile-field">
            <label>Account</label>
            <div
              class="expense-mobile-control"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.ledger_account_id`) }"
            >
              <AccountSelect
                class="field-input m-0 border-none rounded-none w-full"
                v-model="item.ledger_account_id"
                @account-selected="onAccountSelected(index, $event)"
              />
              <div
                v-if="hasError(`expense_items.${index}.ledger_account_id`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.ledger_account_id`) }}
              </div>
            </div>
          </div>

          <div class="expense-mobile-field">
            <label>Description</label>
            <div
              class="expense-mobile-control"
              :class="{ 'border-red-500': hasError(`expense_items.${index}.description`) }"
            >
              <input
                class="field-input w-full border-none rounded-none"
                type="text"
                v-model="item.description"
              />
              <div
                v-if="hasError(`expense_items.${index}.description`)"
                class="px-2 pb-1 text-red-500 text-xs"
              >
                {{ getError(`expense_items.${index}.description`) }}
              </div>
            </div>
          </div>

          <div class="expense-mobile-two-column">
            <div class="expense-mobile-field">
              <label>Tax Code</label>
              <div
                class="expense-mobile-control"
                :class="{ 'border-red-500': hasError(`expense_items.${index}.tax_code`) }"
              >
                <TaxCodeSelect
                  class="field-input m-0 border-none rounded-none w-full"
                  v-model="item.tax_code"
                />
                <div
                  v-if="hasError(`expense_items.${index}.tax_code`)"
                  class="px-2 pb-1 text-red-500 text-xs"
                >
                  {{ getError(`expense_items.${index}.tax_code`) }}
                </div>
              </div>
            </div>

            <div class="expense-mobile-field">
              <label>Amount (Ex. GST)</label>
              <div
                class="expense-mobile-control"
                :class="{ 'border-red-500': hasError(`expense_items.${index}.amount`) }"
              >
                <input
                  class="field-input w-full border-none rounded-none"
                  type="text"
                  inputmode="decimal"
                  v-model="item.amount"
                />
                <div
                  v-if="hasError(`expense_items.${index}.amount`)"
                  class="px-2 pb-1 text-red-500 text-xs"
                >
                  {{ getError(`expense_items.${index}.amount`) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add button -->
      <div class="flex border-gray-500 border-l border-r border-b border-solid">
        <button
          type="button"
          class="flex-grow flex justify-center items-center py-3 px-2 text-sm"
          @click="addNewLine"
        >
          <i class="fa fa-plus mr-1" style="font-size: 0.65rem"></i>
          <span>Add Expense</span>
        </button>
      </div>

      <!-- Footer total -->
      <div class="expense-total-row">
        <div class="expense-total-label">
          Unassigned Expense
        </div>
        <div class="expense-total-value">
          {{ total }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JobSelect from './JobSelect';
import AccountSelect from './AccountSelect';
import TaxCodeSelect from './TaxCodeSelect';
import HiddenFields from '@vueComponents/HiddenFields';
import { sum, last, has, get } from 'lodash';

export default {
  components: {
    JobSelect,
    AccountSelect,
    TaxCodeSelect,
    HiddenFields,
  },
  props: {
    initialItems: Array,
    initialAmount: [Number, String],
    initialGst: [Number, String],
    validationErrors: Object,
  },
  data() {
    return {
      amount: this.initialAmount,
      items: (this.initialItems || []).map(item => ({
        ...item,
        job_id: item.job_id ?? null,
        ledger_account_id: item.ledger_account_id ?? null,
        account_code: item.account_code ?? '',
        account_name: item.account_name ?? '',
        description: item.description ?? '',
        amount: item.amount ?? '',
        tax_code: item.tax_code ?? '',
        auto_description: this.resolveExpenseDescription(
          item.account_code ?? '',
          item.account_name ?? '',
          '',
        ),
      })),
      gst: this.initialGst,
      formElement: null,
    };
  },
  created() {
    if (this.items.length <= 0) {
      this.addNewLine();
    }
  },
  computed: {
    formValue() {
      const formValue = {};
      formValue['amount'] = this.amount;
      formValue['gst'] = this.gst;

      this.items.forEach((item, index) => {
        formValue[`expense_items[${index}][job_id]`] = item.job_id;
        formValue[`expense_items[${index}][ledger_account_id]`] = item.ledger_account_id;
        formValue[`expense_items[${index}][description]`] = item.description;
        formValue[`expense_items[${index}][amount]`] = item.amount;
        formValue[`expense_items[${index}][tax_code]`] = item.tax_code;
      });

      return formValue;
    },
    total() {
      const amount = parseFloat(this.amount) || 0;
      const gst = parseFloat(this.gst) || 0;
      return (amount - gst - this.expenseItemsTotal).toFixed(2);
    },
    expenseItemsTotal() {
      return sum(
        this.items.map(item => parseFloat(item.amount) || 0),
      );
    },
  },
  methods: {
    hasError(name) {
      return has(this.validationErrors || {}, name);
    },
    getError(name) {
      return get(this.validationErrors || {}, name)?.[0];
    },
    addNewLine() {
      const previousAccountCodeId = last(this.items)?.ledger_account_id ?? null;
      const newLength = this.items.push({
        job_id: null,
        ledger_account_id: previousAccountCodeId,
        account_code: '',
        account_name: '',
        description: '',
        amount: '',
        tax_code: '',
        auto_description: '',
      });

      this.focusLine(newLength - 1);
    },
    onAccountSelected(index, selectedAccount) {
      const item = this.items[index];
      if (!item) {
        return;
      }

      const currentDescription = (item.description || '').trim();
      const previousAutoDescription = (item.auto_description || '').trim();
      const hasCustomDescription =
        currentDescription !== '' &&
        currentDescription !== previousAutoDescription;

      const nextAutoDescription = this.resolveExpenseDescription(
        selectedAccount?.code,
        selectedAccount?.name,
        item.description,
      );

      item.account_code = String(selectedAccount?.code || '').trim();
      item.account_name = String(selectedAccount?.name || '').trim();
      item.auto_description = nextAutoDescription;

      if (!hasCustomDescription) {
        item.description = nextAutoDescription;
      }
    },
    resolveExpenseDescription(accountCode, accountDescription, currentDescription) {
      const lookupDescription = this.getAccountCodeDescription(accountCode);
      if (lookupDescription !== null) {
        return lookupDescription;
      }

      const databaseDescription = (accountDescription || '').trim();
      if (databaseDescription !== '') {
        return databaseDescription;
      }

      const existingDescription = (currentDescription || '').trim();
      if (existingDescription !== '') {
        return existingDescription;
      }

      return (accountCode || '').trim();
    },
    getAccountCodeDescription(accountCode) {
      const descriptions = {
        52000: 'Freight & Supplies - Domestic',
        53100: 'Freight - International (GST)',
        53200: 'Freight - International (FRE)',
        54000: 'Temperature control',
        55000: 'Packaging',
        56000: 'Storage',
      };

      const normalizedCode = String(accountCode || '');
      return descriptions[normalizedCode] || null;
    },
    removeLine(index) {
      this.items.splice(index, 1);

      if (this.items.length > 0) {
        this.focusLine(Math.max(0, index - 1));
      }
    },
    focusLine(index) {
      this.$nextTick(() => {
        const isMobile =
          typeof window !== 'undefined' &&
          window.matchMedia('(max-width: 767px)').matches;

        const refName = `${isMobile ? 'mobile' : 'desktop'}FocusMe${index}`;
        const ref = this.$refs[refName];
        const component = Array.isArray(ref) ? ref[0] : ref;

        if (!component) {
          return;
        }

        const element = component.$el || component;
        const focusable =
          element.querySelector?.('input, select, button, [tabindex]') || element;

        if (focusable && typeof focusable.focus === 'function') {
          focusable.focus();
        }
      });
    },
  },
};
</script>

<style>
/** Tom Select */
.ts-control {
  border: none;
  padding: 0;
  color: inherit;
}

/* Summary fields */
.payable-summary-row {
  display: flex;
  width: 100%;
}

.payable-summary-label {
  width: 66.666667%;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #a0aec0;
  border-left: 1px solid #a0aec0;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  background: #f7fafc;
}

.payable-summary-value {
  width: 33.333333%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-top: 1px solid #a0aec0;
  border-left: 1px solid #a0aec0;
  border-right: 1px solid #a0aec0;
  font-size: 0.875rem;
}

/* Desktop table */
.expense-table {
  min-width: 900px;
  width: 100%;
}

.expense-table-row {
  display: flex;
  width: 100%;
}

.expense-cell {
  padding: 0.5rem;
  border-left: 1px solid #a0aec0;
  color: #4a5568;
  font-size: 0.875rem;
  background: #f7fafc;
}

.expense-table-header .expense-col-action {
  border-right: 1px solid #a0aec0;
}

.expense-field-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 1px solid #a0aec0;
  border-top: 1px solid #a0aec0;
  border-bottom: 1px solid #a0aec0;
  font-size: 0.875rem;
  min-width: 0;
}

.expense-action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #a0aec0;
  font-size: 0.875rem;
}

.expense-col-job {
  flex: 0 0 130px;
}

.expense-col-account {
  flex: 1 1 190px;
  min-width: 190px;
}

.expense-col-description {
  flex: 1.35 1 230px;
  min-width: 230px;
}

.expense-col-tax {
  flex: 0 0 145px;
}

.expense-col-amount {
  flex: 0 0 145px;
}

.expense-col-action {
  flex: 0 0 44px;
}

/* Mobile cards hidden on desktop */
.expense-mobile {
  display: none;
}

.expense-mobile-card {
  border-left: 1px solid #a0aec0;
  border-right: 1px solid #a0aec0;
  border-bottom: 1px solid #a0aec0;
  padding: 0.75rem;
}

.expense-mobile-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 600;
}

.expense-mobile-remove {
  color: #f56565;
  font-size: 0.75rem;
  padding: 0.25rem 0;
}

.expense-mobile-field {
  flex: 1 1 0;
  min-width: 0;
  margin-bottom: 0.75rem;
}

.expense-mobile-field label {
  display: block;
  margin-bottom: 0.25rem;
  color: #4a5568;
  font-size: 0.75rem;
  font-weight: 500;
}

.expense-mobile-control {
  border: 1px solid #a0aec0;
  font-size: 0.875rem;
  min-width: 0;
}

.payable-summary-value.border-red-500,
.expense-field-cell.border-red-500,
.expense-mobile-control.border-red-500 {
  border-color: #f56565;
}

.expense-mobile-two-column {
  display: flex;
  gap: 0.75rem;
}

/* Total */
.expense-total-row {
  display: flex;
  width: 100%;
}

.expense-total-label {
  width: 66.666667%;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  border-left: 1px solid #a0aec0;
  border-bottom: 1px solid #a0aec0;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  background: #f7fafc;
}

.expense-total-value {
  width: 33.333333%;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  border-left: 1px solid #a0aec0;
  border-right: 1px solid #a0aec0;
  border-bottom: 1px solid #a0aec0;
  font-size: 0.875rem;
}

@media (max-width: 767px) {
  .payable-summary-row {
    display: block;
  }

  .payable-summary-label,
  .payable-summary-value {
    width: 100%;
  }

  .payable-summary-label {
    justify-content: flex-start;
    border-right: 1px solid #a0aec0;
  }

  .payable-summary-value {
    border-bottom: 0;
  }

  .expense-desktop {
    display: none;
  }

  .expense-mobile {
    display: block;
  }

  .expense-total-label {
    width: 60%;
  }

  .expense-total-value {
    width: 40%;
  }
}

@media (max-width: 420px) {
  .expense-mobile-two-column {
    display: block;
  }
}
</style>
